import {
  ConfigProvider,
  Empty,
  App as AntApp,
  theme as antTheme,
  type ThemeConfig,
} from 'antd';
import zhCN from 'antd/locale/zh_CN';
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type SparkThemeMode = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

interface SparkThemeContextValue {
  mode: SparkThemeMode;
  resolved: ResolvedTheme;
  setMode: (mode: SparkThemeMode) => void;
  toggle: () => void;
}

const SparkThemeContext = createContext<SparkThemeContextValue | null>(null);

const lightTheme: ThemeConfig = {
  algorithm: antTheme.defaultAlgorithm,
  token: {
    colorPrimary: '#176bff',
    colorInfo: '#176bff',
    colorSuccess: '#16a56a',
    colorWarning: '#d9850b',
    colorError: '#d9363e',
    colorBgLayout: '#eef3fb',
    colorBgContainer: 'rgba(255, 255, 255, 0.72)',
    colorBgElevated: 'rgba(255, 255, 255, 0.92)',
    colorBorder: 'rgba(133, 156, 194, 0.28)',
    colorText: '#172033',
    colorTextSecondary: '#59657a',
    borderRadius: 10,
    borderRadiusLG: 14,
    fontFamily:
      'Inter, "SF Pro Display", "PingFang SC", "Microsoft YaHei", system-ui, sans-serif',
    fontSize: 14,
  },
  components: {
    Button: { controlHeight: 38, fontWeight: 500 },
    Input: { controlHeight: 38 },
    Select: { controlHeight: 38 },
    Table: { headerBg: 'rgba(245, 248, 253, 0.68)', headerColor: '#59657a' },
  },
};

const darkTheme: ThemeConfig = {
  algorithm: antTheme.darkAlgorithm,
  token: {
    colorPrimary: '#00cae0',
    colorInfo: '#00cae0',
    colorSuccess: '#35c98a',
    colorWarning: '#f0a935',
    colorError: '#ff6670',
    colorBgLayout: '#0a0a0a',
    colorBgContainer: '#111318',
    colorBgElevated: '#181d27',
    colorBorder: 'rgba(255, 255, 255, 0.1)',
    colorText: '#f5fbff',
    colorTextSecondary: '#a6afc0',
    colorTextLightSolid: '#031418',
    borderRadius: 10,
    borderRadiusLG: 14,
    fontFamily:
      'Inter, "SF Pro Display", "PingFang SC", "Microsoft YaHei", system-ui, sans-serif',
    fontSize: 14,
  },
  components: {
    Button: { controlHeight: 38, fontWeight: 500 },
    Input: { controlHeight: 38 },
    Select: { controlHeight: 38 },
    Table: { headerBg: '#181d27', headerColor: '#a6afc0', borderColor: 'rgba(255, 255, 255, 0.1)' },
  },
};

const getSystemTheme = (): ResolvedTheme =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

export function SparkThemeProvider({ children, defaultMode = 'dark' }: { children: ReactNode; defaultMode?: SparkThemeMode }) {
  const [mode, setMode] = useState<SparkThemeMode>(() => {
    const saved = localStorage.getItem('spark-theme');
    return saved === 'light' || saved === 'dark' || saved === 'system'
      ? saved
      : defaultMode;
  });
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);
  const resolved = mode === 'system' ? systemTheme : mode;

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => setSystemTheme(media.matches ? 'dark' : 'light');
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  useLayoutEffect(() => {
    localStorage.setItem('spark-theme', mode);
    document.documentElement.dataset.theme = resolved;
    document.documentElement.style.colorScheme = resolved;
  }, [mode, resolved]);

  const value = useMemo<SparkThemeContextValue>(
    () => ({
      mode,
      resolved,
      setMode,
      toggle: () => setMode(resolved === 'dark' ? 'light' : 'dark'),
    }),
    [mode, resolved],
  );

  return (
    <SparkThemeContext.Provider value={value}>
      <ConfigProvider
        locale={zhCN}
        modal={{ centered: true, mask: { closable: false } }}
        renderEmpty={() => <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />}
        theme={resolved === 'dark' ? darkTheme : lightTheme}
      >
        <AntApp>{children}</AntApp>
      </ConfigProvider>
    </SparkThemeContext.Provider>
  );
}

export function useSparkTheme() {
  const value = useContext(SparkThemeContext);
  if (!value) {
    throw new Error('useSparkTheme must be used inside SparkThemeProvider');
  }
  return value;
}
